/**
 * Request list primary label (#1104): project name when populated, else customer name.
 */

export type RequestListLabelSource = {
  oppProjectName?: string | null;
  customerName?: string | null;
};

export function getRequestListPrimaryLabel(
  request: RequestListLabelSource,
): string {
  const project = request.oppProjectName?.trim();
  if (project) {
    return project;
  }
  return (request.customerName ?? '').trim();
}

export function requestListShowsProjectName(
  request: RequestListLabelSource,
): boolean {
  return Boolean(request.oppProjectName?.trim());
}

/**
 * Request list row label for assistive tech (#1575 FR-5).
 *
 * The unread state is carried by WEIGHT, which a screen reader does not
 * announce, so the row says it in words. One function for both renderers on
 * purpose: the web table row and the mobile card must not be able to drift
 * into saying different things about the same row.
 *
 * `hasUnseenChanges` is read with `=== true` — absent (degraded stamp, older
 * server, cached row) announces as read, exactly as it draws.
 */
export function getRequestRowAccessibilityLabel(
  request: RequestListLabelSource & {
    id?: string | null;
    hasUnseenChanges?: boolean;
  },
): string {
  const primary = getRequestListPrimaryLabel(request);
  const id = (request.id ?? '').toString().trim();
  const base = [id ? `Request ${id}` : 'Request', primary]
    .filter(Boolean)
    .join(', ');
  return request.hasUnseenChanges === true ? `${base}, unread changes` : base;
}

/**
 * Is this id a CLIENT key rather than a server-issued request number?
 *
 * `offlineCache.getNextOfflineId` mints two shapes: `o-<counter>` normally, and
 * `offline_<ts>_<rand>` from its catch path when the counter store throws.
 * Neither identifies anything outside the device that made it, so neither may
 * ever be rendered as a request number — a requestor who quotes one in an
 * email is quoting something nobody else can look up.
 *
 * One predicate, used by BOTH toolbar strings on purpose. They disagreed
 * before: the number line rejected these keys while the title happily printed
 * `Request #o-1760000000000` for the same request (#1531).
 */
function isOfflineClientKey(id: string): boolean {
  return id.startsWith('o-') || id.startsWith('offline_');
}

/**
 * Request detail toolbar: project/customer name when populated, else Request #id (#1104 parity).
 *
 * An offline draft with no name falls through to the bare `Request` — never
 * `Request #<client key>`. See {@link isOfflineClientKey}.
 */
export function getRequestDetailToolbarTitle(
  request: RequestListLabelSource & { id?: string | null },
  requestId?: string | null,
): string {
  const primary = getRequestListPrimaryLabel(request);
  if (primary) {
    return primary;
  }

  const id = (request.id ?? requestId ?? '').toString().trim();
  return id && !isOfflineClientKey(id) ? `Request #${id}` : 'Request';
}

/**
 * Request detail toolbar: the request number shown on its own identity line
 * under the title (#1531).
 *
 * Returns the bare id — the toolbar owns the `Request #…` wording — or `null`
 * when the line must not render:
 *
 * - **No primary label.** {@link getRequestDetailToolbarTitle} already falls
 *   back to `Request #id`, so a line here would say it twice.
 * - **A client key.** See {@link isOfflineClientKey}. An offline draft has no
 *   request number yet, so it shows none — the same invariant the title now
 *   holds to.
 */
export function getRequestDetailToolbarRequestNumber(
  request: RequestListLabelSource & { id?: string | null },
  requestId?: string | null,
): string | null {
  if (!getRequestListPrimaryLabel(request)) {
    return null;
  }

  const id = (request.id ?? requestId ?? '').toString().trim();
  if (!id || isOfflineClientKey(id)) {
    return null;
  }
  return id;
}

/**
 * Label for the Correspondence/Discussion panel (#1531 review follow-up).
 *
 * Same invariant as the two toolbar strings: a client key is never printed as
 * a request number. This surface is reachable for one, by two routes that the
 * toolbar's own `!requestId.startsWith('o-')` gate does not cover:
 *
 * 1. `discussionOpen` seeds from the `?discussion=` query param, so a link
 *    opens the panel without the gated toolbar toggle ever rendering.
 * 2. That gate tests only `o-`. An `offline_…` id from `getNextOfflineId`'s
 *    catch path walks straight past it and the toggle renders as normal.
 *
 * Returns `undefined` for an empty id so callers keep passing nothing, which
 * is what they did before.
 */
export function getRequestCorrespondenceLabel(
  requestId?: string | null,
): string | undefined {
  const id = (requestId ?? '').toString().trim();
  if (!id) {
    return undefined;
  }
  return isOfflineClientKey(id) ? 'Request' : `Request #${id}`;
}
